import {inferSchedule} from 'common/benefitSchedule';
import {fromSeconds} from 'common/date';
import {AlertTriangle, ChevronDown, ChevronRight} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {Button} from 'src/Button';
import {BenefitTransactionsList} from 'src/CardBenefitsView/BenefitTransactionsList';
import {
  formatCents,
  formatCentsShort,
  formatCentsShortSigned,
  formatCentsSigned,
  formatPeriodLabel,
} from 'src/CardBenefitsView/benefitUtils';
import {
  buildFeeYearAxis,
  monthOffset,
  monthOffsetOf,
  summarizeBenefit,
} from 'src/CardBenefitsView/benefitWindows';
import {useModals} from 'src/Modals';
import type {
  FullCardBenefitFragment,
  FullFeePeriodFragment,
} from '@transactions/graphql';
import type {
  BenefitSummary,
  BenefitWindow,
  FeeYearAxis,
} from 'src/CardBenefitsView/benefitWindows';

const CADENCE_SHORT: Record<string, string> = {
  monthly: 'mo',
  quarterly: 'qtr',
  semi_annual: 'half',
  annual: 'yr',
};

/**
 * One-line schedule summary for the row's subtitle: "$15/mo ($35 Dec)",
 * falling back to the total for irregular rows.
 */
const scheduleLabel = (
  benefit: FullCardBenefitFragment,
  feePeriod: FullFeePeriodFragment,
  totalAmount: number,
): string => {
  const schedule = inferSchedule(benefit.periods, {
    startDate: feePeriod.startDate,
    endDate: feePeriod.endDate,
  });
  if (schedule == null) {
    return `${formatCentsShort(totalAmount)} total`;
  }
  const base = `${formatCentsShort(schedule.amountPerPeriod)}/${CADENCE_SHORT[schedule.cadence]}`;
  if (schedule.exceptions.length === 0) {
    return base;
  }
  // Every exception, not just the first — the editor permits several. A
  // multi-year period can hold two exceptions in the same-named month
  // ("Dec ’25, Dec ’26"); append years only when needed to tell them apart.
  const monthNames = schedule.exceptions.map((exception) =>
    fromSeconds(exception.startDate).toLocaleString({month: 'short'}),
  );
  const exceptions = schedule.exceptions
    .map((exception, index) => {
      const month = monthNames[index];
      const ambiguous = monthNames.some(
        (name, other) => other !== index && name === month,
      );
      const year = fromSeconds(exception.startDate).year;
      return `${formatCentsShort(exception.amount)} ${month}${
        ambiguous ? ` ’${String(year).slice(2)}` : ''
      }`;
    })
    .join(', ');
  return `${base} (${exceptions})`;
};

/** Sort key: rows needing action first, in-rhythm rows next, resolved last */
const rowRank = (summary: BenefitSummary): number =>
  summary.nextOpenWindow != null ? 0 : summary.nextFutureWindow != null ? 1 : 2;

const rowSortValue = (summary: BenefitSummary): number =>
  summary.nextOpenWindow?.endDate ??
  summary.nextFutureWindow?.startDate ??
  Number.MAX_SAFE_INTEGER;

type CellProps = {
  window: BenefitWindow;
  axis: FeeYearAxis;
};

const WindowCell = ({window, axis}: CellProps) => {
  const startOffset = monthOffset(axis, window.startDate);
  const endOffset = monthOffset(axis, window.endDate);
  const clampedStart = Math.max(0, startOffset);
  const clampedEnd = Math.min(axis.monthCount, endOffset);
  if (clampedEnd <= clampedStart) {
    return null;
  }
  const left = (clampedStart / axis.monthCount) * 100;
  const width = ((clampedEnd - clampedStart) / axis.monthCount) * 100;
  const clippedLeft = startOffset < -0.05;
  const clippedRight = endOffset > axis.monthCount + 0.05;
  // Wide cells carry a text label; month-sized cells rely on tint + tooltip
  const wide = clampedEnd - clampedStart >= 1.8;

  const usedPct =
    window.amount > 0
      ? Math.max(
          0,
          Math.min(100, Math.round((window.usedAmount / window.amount) * 100)),
        )
      : 0;

  let cellClass: string;
  let label: React.ReactNode = null;
  let fill: React.ReactNode = null;

  switch (window.state) {
    case 'over':
      cellClass = 'bg-destructive/15 ring-1 ring-inset ring-destructive/50';
      label = (
        <span className="text-destructive">{wide ? 'over cap' : '!'}</span>
      );
      break;
    case 'captured':
      cellClass = 'bg-green-500/15 ring-1 ring-inset ring-green-500/25';
      if (wide) {
        label = (
          <span className="text-positive/90">
            ✓ {formatCentsShort(window.usedAmount)}
          </span>
        );
      }
      break;
    case 'booked':
      cellClass = 'stripe-booked ring-1 ring-inset ring-green-500/30';
      label = (
        <span className="text-positive/80">
          {wide ? `↻ ${formatCentsShort(window.amount)}` : '↻'}
        </span>
      );
      break;
    case 'open':
      cellClass = 'bg-blue-500/10 ring-2 ring-inset ring-blue-400/70';
      if (window.usedAmount > 0) {
        fill = (
          <span
            className="absolute inset-y-0 left-0 bg-green-500/20"
            style={{width: `${usedPct}%`}}
          />
        );
      }
      label = (
        <span className="text-blue-600 dark:text-blue-300">
          {wide
            ? `${formatCentsShort(window.openRemaining)} · ${window.daysLeft}d`
            : `${window.daysLeft}d`}
        </span>
      );
      break;
    case 'partial':
      cellClass = 'ring-1 ring-inset ring-amber-500/30 bg-transparent';
      fill = (
        <>
          <span
            className="absolute inset-y-0 left-0 bg-green-500/15"
            style={{width: `${usedPct}%`}}
          />
          <span
            className="stripe-missed absolute inset-y-0 right-0"
            style={{width: `${100 - usedPct}%`}}
          />
        </>
      );
      if (wide) {
        label = (
          <span className="text-amber-700 dark:text-amber-400">
            {formatCentsShort(window.usedAmount)} · −
            {formatCentsShort(window.missedAmount)}
          </span>
        );
      }
      break;
    case 'missed':
      cellClass = 'bg-amber-500/10 ring-1 ring-inset ring-amber-500/35';
      if (wide) {
        label = (
          <span className="text-amber-700 dark:text-amber-400">
            −{formatCentsShort(window.missedAmount)}
          </span>
        );
      }
      break;
    case 'future':
      cellClass = 'bg-muted/40';
      break;
  }

  const stateNote =
    window.state === 'over'
      ? ' — exceeds window amount'
      : window.state === 'booked'
        ? ` — ${formatCents(window.bookedAmount)} booked ahead`
        : window.state === 'open'
          ? ` — ${formatCents(window.openRemaining)} claimable, ${window.daysLeft} days left`
          : window.missedAmount > 0
            ? ` — ${formatCents(window.missedAmount)} expired unused`
            : '';
  const title = `${formatPeriodLabel(window.startDate, window.endDate)}: ${formatCentsSigned(window.usedAmount)} / ${formatCents(window.amount)}${stateNote}`;

  const clipStyle: React.CSSProperties = {};
  if (clippedLeft || clippedRight) {
    const from = clippedLeft ? 'transparent 0, black 12px' : 'black 0';
    const to = clippedRight
      ? 'black calc(100% - 12px), transparent 100%'
      : 'black 100%';
    clipStyle.maskImage = `linear-gradient(90deg, ${from}, ${to})`;
  }

  return (
    <div
      title={title}
      className={`absolute inset-y-0 overflow-hidden rounded-[4px] ${cellClass}`}
      style={{
        left: `${left}%`,
        // Floor at 3px so a legitimate one-day window stays visible
        width: `max(calc(${width}% - 2px), 3px)`,
        ...clipStyle,
      }}
    >
      {fill}
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold whitespace-nowrap">
        {label}
      </span>
    </div>
  );
};

type RowProps = {
  summary: BenefitSummary;
  feePeriod: FullFeePeriodFragment;
  axis: FeeYearAxis;
  todayPct: number | null;
  onUpdate: () => void;
};

const BenefitRow = (props: RowProps) => {
  const {summary, feePeriod, axis, todayPct, onUpdate} = props;
  const {benefit} = summary;
  const {
    editCardBenefitModal,
    deleteCardBenefitModal,
    createTransactionForBenefitModal,
    generateBenefitCreditsModal,
  } = useModals();
  const [expanded, setExpanded] = React.useState(false);

  const transactions = React.useMemo(
    () =>
      benefit.periods
        .flatMap((period) => period.rawTransactions)
        .sort((a, b) => b.date.localeCompare(a.date)),
    [benefit.periods],
  );

  // Secondary summary: deviations first (missed, open), then booked, then a
  // quiet "complete" for fully-resolved rows
  const summaryParts: React.ReactNode[] = [];
  if (summary.missed > 0) {
    summaryParts.push(
      <span key="missed" className="text-amber-600 dark:text-amber-400">
        −{formatCentsShort(summary.missed)}
      </span>,
    );
  }
  if (summary.open > 0) {
    summaryParts.push(
      <span key="open" className="text-blue-600 dark:text-blue-300">
        {formatCentsShort(summary.open)} open
      </span>,
    );
  }
  if (summary.hasOver) {
    summaryParts.push(
      <span key="over" className="text-destructive">
        over cap
      </span>,
    );
  }
  if (summaryParts.length < 2 && summary.booked > 0) {
    summaryParts.push(
      <span key="booked" className="text-positive/90">
        {formatCentsShort(summary.booked)} booked
      </span>,
    );
  }
  if (
    summaryParts.length === 0 &&
    summary.totalAmount > 0 &&
    summary.totalUsed >= summary.totalAmount
  ) {
    summaryParts.push(
      <span key="complete" className="text-positive/90">
        complete
      </span>,
    );
  }

  return (
    <div className="border-border border-t">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="hover:bg-accent/20 grid w-full cursor-pointer grid-cols-[11rem_1fr_7.5rem] items-center gap-3 px-4 py-2 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            {expanded ? (
              <ChevronDown className="text-muted-foreground size-3 shrink-0" />
            ) : (
              <ChevronRight className="text-muted-foreground size-3 shrink-0" />
            )}
            <span className="truncate text-[13px] font-medium">
              {benefit.name}
            </span>
            {benefit.uncoveredRawTransactions.length > 0 && (
              <AlertTriangle className="size-3 shrink-0 text-amber-600 dark:text-amber-400" />
            )}
          </div>
          <p className="text-muted-foreground truncate pl-[18px] text-[11px]">
            {scheduleLabel(benefit, feePeriod, summary.totalAmount)}
            {benefit.creditAccount != null && ` → ${benefit.creditAccount.name}`}
          </p>
        </div>

        <div className="relative h-6">
          {summary.windows.map((window) => (
            <WindowCell key={window.startDate} window={window} axis={axis} />
          ))}
          {todayPct != null && (
            <span
              className="bg-foreground/40 absolute inset-y-[-3px] w-px"
              style={{left: `${todayPct}%`}}
            />
          )}
        </div>

        <div className="text-right text-xs tabular-nums">
          <div>
            <span className="font-semibold">
              {formatCentsShortSigned(summary.totalUsed)}
            </span>
            <span className="text-muted-foreground">
              {' '}
              / {formatCentsShort(summary.totalAmount)}
            </span>
          </div>
          {summaryParts.length > 0 && (
            <div className="text-[11px]">
              {summaryParts.flatMap((part, index) =>
                index === 0
                  ? [part]
                  : [
                      <span key={`sep-${index}`} className="text-muted-foreground">
                        {' · '}
                      </span>,
                      part,
                    ],
              )}
            </div>
          )}
        </div>
      </button>

      {expanded && (
        <div className="flex flex-col gap-3 px-4 pt-1 pb-3 pl-[38px]">
          {benefit.description != null && benefit.description !== '' && (
            <p className="text-muted-foreground text-xs">{benefit.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="px-2.5 py-1 text-xs"
              onClick={() =>
                createTransactionForBenefitModal.openModal({
                  benefit,
                  feePeriodPayee: feePeriod.payee,
                  onSuccess: onUpdate,
                })
              }
            >
              Record use
            </Button>
            {benefit.creditAccountId != null && (
              <Button
                variant="outline"
                className="px-2.5 py-1 text-xs"
                onClick={() =>
                  generateBenefitCreditsModal.openModal({
                    benefit,
                    feePeriodPayee: feePeriod.payee,
                    onSuccess: onUpdate,
                  })
                }
              >
                Create credits
              </Button>
            )}
            <Button
              variant="outline"
              className="px-2.5 py-1 text-xs"
              onClick={() =>
                editCardBenefitModal.openModal({
                  benefit,
                  feePeriodStartDate: feePeriod.startDate,
                  feePeriodEndDate: feePeriod.endDate,
                  onSuccess: onUpdate,
                })
              }
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              className="text-destructive px-2.5 py-1 text-xs"
              onClick={() =>
                deleteCardBenefitModal.openModal({
                  benefit,
                  onDeleted: onUpdate,
                })
              }
            >
              Delete
            </Button>
          </div>

          {transactions.length > 0 && (
            <div>
              <p className="text-muted-foreground mb-1 text-[11px] font-medium tracking-wide uppercase">
                {transactions.length} linked transaction
                {transactions.length === 1 ? '' : 's'}
              </p>
              <BenefitTransactionsList
                rawTransactions={transactions}
                benefitName={benefit.name}
                onUpdate={onUpdate}
              />
            </div>
          )}

          {benefit.uncoveredRawTransactions.length > 0 && (
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="size-3.5 shrink-0" />
                {formatCentsSigned(benefit.uncoveredAmount)} in linked
                transactions outside every window
              </p>
              <BenefitTransactionsList
                rawTransactions={benefit.uncoveredRawTransactions}
                benefitName={benefit.name}
                onUpdate={onUpdate}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

type Props = {
  feePeriod: FullFeePeriodFragment;
  onUpdate: () => void;
};

/**
 * The fee year as a real calendar: one labeled month column per month of the
 * fee year, every benefit as a row whose windows sit where they belong in
 * time, and a today line. Quiet where money was captured, loud only where it
 * is still claimable or was missed.
 */
export const FeeYearGrid = ({feePeriod, onUpdate}: Props) => {
  // Time-dependent throughout (window states shift as the clock runs), so
  // compute per render rather than memoizing on feePeriod alone
  const now = DateTime.now();
  const axis = buildFeeYearAxis(feePeriod);
  const summaries = feePeriod.benefits
    .map((benefit) => summarizeBenefit(benefit, now))
    .sort(
      (a, b) =>
        rowRank(a) - rowRank(b) ||
        rowSortValue(a) - rowSortValue(b) ||
        b.totalAmount - a.totalAmount,
    );

  const todayOffset = monthOffsetOf(axis, now);
  const todayPct =
    todayOffset >= 0 && todayOffset <= axis.monthCount
      ? (todayOffset / axis.monthCount) * 100
      : null;

  return (
    // Fixed name/summary columns plus ~1.7rem per month column; the parent
    // provides overflow-x scrolling when the viewport is narrower
    <div style={{minWidth: `${20 + axis.monthCount * 1.7}rem`}}>
      <div className="text-muted-foreground grid grid-cols-[11rem_1fr_7.5rem] items-end gap-3 px-4 pb-1.5">
        <div />
        <div className="relative">
          <div
            className="grid text-center text-[9px] font-medium tracking-wider"
            style={{
              gridTemplateColumns: `repeat(${axis.monthCount}, minmax(0, 1fr))`,
            }}
          >
            {axis.labels.map((label, index) => (
              <span key={index}>{label}</span>
            ))}
          </div>
          {todayPct != null && (
            <span
              className="text-foreground/70 absolute -top-3 -translate-x-1/2 text-[9px] font-semibold tracking-wider whitespace-nowrap"
              style={{left: `${todayPct}%`}}
            >
              {/* UTC to match the marker position (axis math is UTC) */}
              {now
                .toUTC()
                .toLocaleString({month: 'short', day: 'numeric'})
                .toUpperCase()}
            </span>
          )}
        </div>
        <div className="text-right text-[10px]">captured / cap</div>
      </div>
      {summaries.map((summary) => (
        <BenefitRow
          key={summary.benefit._id}
          summary={summary}
          feePeriod={feePeriod}
          axis={axis}
          todayPct={todayPct}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
};
